/**
 * Domains that are one operation wearing several names.
 *
 * Google's copyright dump makes these visible in a way nothing else does. Six
 * of them carry almost exactly the same number of reported URLs:
 *
 *     mrunblock.bar        1 894 370
 *     unbl0ck.icu          1 894 238
 *     u4m.sbs              1 894 235
 *     unbl4you.cyou        1 894 204
 *     unblockproject.top   1 894 179
 *     unblocknow.surf      1 894 165
 *
 * Identical inventory behind six addresses. The same shape repeats for redwap,
 * xtapes, pornxp, biqle and the tracker proxies.
 *
 * Why this matters more than the site list: without it, a removal against one
 * domain is reported to the customer as a removal, while the same material
 * stays up on five others. She sees "removed" beside a link that opens. There
 * is no faster way to lose somebody's trust than to be caught telling them
 * their problem is solved when it is not.
 *
 * Grouping costs nothing at scan time -- it is a lookup over data already
 * stored -- and it changes what we can honestly say about a takedown.
 */
export interface MirrorFamily {
  /** What to call the operation when speaking to the customer. */
  label: string;
  domains: readonly string[];
  /**
   * Whether the sweep buys a `site:` chain for every door of this family.
   * True where the family hosts the content: one door searched and three not
   * finds it where we have already filed. False where the doors rotate faster
   * than a chain is worth -- still filed against, just not searched for.
   */
  searched: boolean;
}

export const MIRROR_FAMILIES: readonly MirrorFamily[] = [
  {
    label: 'Redwap',
    searched: true,
    domains: ['redwap.me', 'redwap2.com', 'redwap3.com', 'redwap.sex'],
  },
  {
    label: 'Xtapes',
    searched: true,
    domains: ['xtapes.in', 'xtapes.to', 'xtapes.tw'],
  },
  {
    label: 'PornXP',
    searched: true,
    domains: ['pornxp.com', 'pornxp.org', 'pornxp.cc', 'pornxp.me'],
  },
  {
    label: 'Biqle',
    searched: true,
    domains: ['biqle.com', 'biqle.ru', 'biqle.org'],
  },
  {
    label: 'Siska',
    searched: true,
    domains: ['siska.tv', 'siska.video'],
  },
  {
    label: 'Vidz',
    searched: true,
    domains: ['vidz7.com', 'vidz24.com'],
  },
  {
    // Not a site of its own: a rotating set of front doors to blocked
    // trackers. Whichever one is taken down, the rest keep serving.
    label: 'Tracker proxies',
    searched: false,
    domains: [
      'proxybit.sbs',
      'mrunblock.bar',
      'unbl0ck.icu',
      'u4m.sbs',
      'unbl4you.cyou',
      'unblockproject.top',
      'unblocknow.surf',
      'unblocked.lol',
      'unblocked.pw',
      'unblocked.casa',
      'unblocked.to',
      'unblocked.is',
      'unblock.re',
      '123unblock.world',
      '123unblock.bar',
      'nocensor.biz',
      'proxyrarbg.org',
      'thepiratebay.party',
      'g3g.fun',
    ],
  },
];

/** Every domain that belongs to some family, mapped to that family. */
const BY_DOMAIN = new Map<string, MirrorFamily>(
  MIRROR_FAMILIES.flatMap((family) => family.domains.map((domain) => [domain, family] as const)),
);

/**
 * The other addresses the same operation serves this content from.
 *
 * Excludes the family member the host belongs to, not the host itself. Those
 * differ whenever a finding sits on a subdomain: `cdn.biqle.ru` is not equal to
 * `biqle.ru`, so filtering on the raw host left the site's own domain in its
 * own list of mirrors, and the customer was told to worry about a page they
 * were already looking at.
 */
export function siblingDomains(domain: string): string[] {
  const match = matchFamily(domain);
  if (!match) return [];
  return match.family.domains.filter((sibling) => sibling !== match.member);
}

/** The family a host belongs to, and which member of it matched. */
function matchFamily(domain: string): { family: MirrorFamily; member: string } | null {
  const host = domain.trim().toLowerCase().replace(/^www\./, '');

  const direct = BY_DOMAIN.get(host);
  if (direct) return { family: direct, member: host };

  for (const [known, family] of BY_DOMAIN) {
    if (host.endsWith(`.${known}`)) return { family, member: known };
  }
  return null;
}
